/**
 * wireGlobalListeners — popstate handler + cross-tab storage events.
 *
 * Called once after the shell mounts and the initial dispatch has run.
 *
 * Handles:
 *   - popstate: re-dispatch on browser back/forward
 *   - storage 'activeOrg': switchOrg (cross-tab)
 *   - storage 'activeWorkspace': switchWorkspace (cross-tab)
 *   - storage 'activeWorkspaceProject': switchWorkspaceProject (cross-tab)
 *   - storage 'logout': clear all state + redirect to /login
 *   - storage 'themeMode': update <html data-theme> + state
 *   - storage 'lang': call setLang with skipPersist (avoid echo loop)
 */

import { changeGlobalTheme } from 'smbls'
import { dispatchUrl } from './dispatch.js'
import { switchOrg } from '../shared/functions/switchers/switchOrg.js'
import { switchWorkspace } from '../shared/functions/switchers/switchWorkspace.js'
import { switchWorkspaceProject } from '../shared/functions/switchers/switchWorkspaceProject.js'
import { logout } from '../shared/functions/switchers/logout.js'

// Resolve the scratch theme token to a concrete 'light' | 'dark' value.
// 'auto' defers to the OS preference.
const resolveTheme = (mode) => {
  if (mode === 'dark') return 'dark'
  if (mode === 'light') return 'light'
  // 'auto'
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * @param {object} shell           - root shell element from create()
 * @param {object} opts
 * @param {object} opts.apps       - app roster (Phase 7 stubs or Phase 8 manifests)
 * @param {string} opts.fallbackRoute
 */
export const wireGlobalListeners = (shell, { apps, fallbackRoute = '/' } = {}) => {
  // --- popstate ---
  window.addEventListener('popstate', async () => {
    const path = window.location.pathname + window.location.search
    await dispatchUrl(shell, path)
  })

  // --- cross-tab storage events ---
  window.addEventListener('storage', async (e) => {
    // Null new value means the key was removed — ignore.
    if (e.newValue === null) return

    const state = shell.state?.root || shell.state
    // Lazy-resolve SDK + queryClient from context so we don't need to pass
    // them as parameters. The shell's context is attached by bootShell.
    const sdk = shell.context?.sdk || null
    const queryClient = shell.context?.queryClient || null

    if (e.key === 'activeOrg' && state?.org?.id !== e.newValue) {
      if (sdk && queryClient) {
        await switchOrg(state, sdk, queryClient, e.newValue)
      }
    }

    if (e.key === 'activeWorkspace' && state?.workspace?.id !== e.newValue) {
      if (sdk && queryClient) {
        await switchWorkspace(state, sdk, queryClient, e.newValue)
      }
    }

    if (e.key === 'activeWorkspaceProject' && state?.workspaceProject?.id !== e.newValue) {
      if (sdk && queryClient) {
        await switchWorkspaceProject(state, sdk, queryClient, e.newValue)
      }
    }

    if (e.key === 'logout') {
      // Another tab signed out — mirror the logout locally.
      if (sdk && queryClient) {
        await logout(state, sdk, queryClient)
      } else {
        window.history.replaceState(null, '', '/login')
      }
    }

    if (e.key === 'themeMode') {
      const resolved = resolveTheme(e.newValue)
      changeGlobalTheme(resolved)
      if (state) state.themeMode = e.newValue
    }

    if (e.key === 'lang' && e.newValue && state?.lang !== e.newValue) {
      // setLang is a shared function — invoke via el.call convention once
      // the shell is fully mounted. For cross-tab events we call it directly.
      const setLang = shell.context?.methods?.setLang
      if (typeof setLang === 'function') {
        await setLang(e.newValue, { skipPersist: true })
      } else if (state) {
        state.lang = e.newValue
      }
    }
  })
}
